/** The company playbook as the UI shows it. Pure for testing. */
import { companyPath } from "./routes";
import {
  PLAYBOOK_CATEGORIES,
  type Company,
  type PlaybookAgent,
  type PlaybookCategory,
  type PlaybookRule,
  type PlaybookRuleInput
} from "./types";

export const CATEGORY_LABEL: Record<PlaybookCategory, string> = {
  filing: "Filing",
  communication: "Communication",
  drafting: "Drafting",
  approvals: "Approvals",
  green_card: "Green card"
};

/** Where Casey follows a rule: on cases, while drafting, or practice-wide. */
export const AGENT_LABEL: Record<PlaybookAgent, string> = {
  case_agent: "Cases",
  drafting: "Drafting",
  practice_manager: "Practice"
};

export const liveRules = (rules: PlaybookRule[]): PlaybookRule[] =>
  rules.filter((r) => r.status === "active");

export const suggestedRules = (rules: PlaybookRule[]): PlaybookRule[] =>
  rules.filter((r) => r.status === "suggested");

const TITLE_WORDS = 7;

/** A rule's title, from its own text: its first clause, a few words long. */
export const ruleTitle = (text: string): string => {
  const clause =
    text
      .trim()
      .split(/(?<=[.!?;:])\s|\s[–—-]\s/)[0]
      ?.replace(/[.!?;:,]+$/, "") ?? "";
  const words = clause.split(/\s+/).filter(Boolean);
  if (!words.length) return "";
  const head = words.slice(0, TITLE_WORDS).join(" ");
  const title = head.charAt(0).toUpperCase() + head.slice(1);
  return words.length > TITLE_WORDS ? `${title.replace(/[,;:]$/, "")}…` : title;
};

/** "Rule 3: Letterhead on every support letter", as Casey cites it. */
export const ruleLabel = (
  rule: Pick<PlaybookRule, "number" | "text">
): string | null => {
  if (!rule.number) return null;
  const title = ruleTitle(rule.text);
  return title ? `Rule ${rule.number}: ${title}` : `Rule ${rule.number}`;
};

export const appliesText = (rule: Pick<PlaybookRule, "applies_to">): string =>
  rule.applies_to.length ? rule.applies_to.join(", ") : "All cases";

export const byCategory = (
  rules: PlaybookRule[]
): Array<{ category: PlaybookCategory; rules: PlaybookRule[] }> =>
  PLAYBOOK_CATEGORIES.map((category) => ({
    category,
    rules: rules
      .filter((r) => r.category === category)
      .sort((a, b) => a.number - b.number)
  })).filter((g) => g.rules.length > 0);

export const toInput = (rule: PlaybookRule): PlaybookRuleInput => ({
  id: rule.id,
  number: rule.number,
  category: rule.category,
  text: rule.text,
  applies_to: rule.applies_to,
  source: rule.source,
  agents: rule.agents,
  enabled: rule.enabled,
  reason: rule.reason
});

/** The live list to PUT with one rule changed (or removed, for `null`). */
export const liveWith = (
  rules: PlaybookRule[],
  id: string,
  patch: PlaybookRuleInput | null
): PlaybookRuleInput[] =>
  liveRules(rules).flatMap((r) =>
    r.id !== id ? [toInput(r)] : patch ? [{ ...toInput(r), ...patch }] : []
  );

export const rulePath = (companyId: string, number: number): string =>
  `${companyPath(companyId, "playbook")}&rule=${number}`;

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Code spans, code blocks and existing links: left as they are. */
const PROTECTED = /(```[\s\S]*?```|`[^`\n]*`|\[[^\]\n]*\]\([^)\n]*\))/;

const RULE_TITLE_CHARS = 160;

/**
 * Turn "Northwind 7" in agent prose into a link to the rule, so the chat
 * renders it as a citation chip with the rule's text on hover. Only the
 * company's live rule numbers are linked; code and existing links are left
 * alone.
 */
export const linkRuleCitations = (
  text: string,
  company: Pick<Company, "id" | "name" | "playbook"> | null | undefined
): string => {
  if (!company?.name) return text;
  if (!text.includes(company.name) && !/\brule \d/i.test(text)) return text;
  const live = new Map(
    liveRules(company.playbook ?? []).map((r) => [r.number, r])
  );
  if (!live.size) return text;
  const pattern = new RegExp(
    `(?<![\\w/\\[])(?:${escapeRegExp(
      company.name
    )}|[Rr]ule) (\\d{1,4})(?![\\w\\]])`,
    "g"
  );
  return text
    .split(PROTECTED)
    .map((chunk, i) =>
      i % 2 === 1
        ? chunk
        : chunk.replace(pattern, (match, n: string) => {
            const rule = live.get(Number(n));
            if (!rule) return match;
            const body =
              rule.text.length > RULE_TITLE_CHARS
                ? `${rule.text.slice(0, RULE_TITLE_CHARS - 1)}…`
                : rule.text;
            const title = [
              "Playbook rule",
              CATEGORY_LABEL[rule.category],
              body.replace(/"/g, "'")
            ].join(" · ");
            return `[${match}](${rulePath(
              company.id,
              rule.number
            )} "${title}")`;
          })
    )
    .join("");
};
